// ===========================================
// Turnstile submit gating for the public forms (#412)
// ===========================================
//
// Holds the widget's token and decides when Submit may be pressed. The SERVER
// decides whether a submission is accepted (plan md/plan/412.md D10), so Submit
// is enabled once there is:
//   - a token; or
//   - a widget that reported it cannot run (error / unsupported); or
//   - 10 s with no token EVER (an ad-blocker can hide the widget silently); or
//   - 30 s with no new token after a reset (Cloudflare went silent mid-visit, so
//     the family is never stuck behind a disabled button until a reload).
// Without a token the form sends '' and Cloudflare's answer decides; a refusal
// comes back as 400 `SPAM_CHECK_FAILED`, which the page explains.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { TurnstileWidgetHandle } from '../components/public/TurnstileWidget';

export const TURNSTILE_WAIT_MS = 10_000;
/** After a reset, how long to wait for the next token before letting the server decide. */
export const TURNSTILE_RESET_WAIT_MS = 30_000;

export const SPAM_CHECK_FAILED = 'SPAM_CHECK_FAILED';

/** True when a request was refused by the server's Turnstile check (switch on the CODE, never the text). */
export function isSpamCheckFailed(error: unknown): boolean {
  const details = (error as { response?: { data?: { details?: { code?: unknown } } } })?.response?.data?.details;
  return details?.code === SPAM_CHECK_FAILED;
}

export function useTurnstileGate(siteKey: string | null | undefined) {
  const widgetRef = useRef<TurnstileWidgetHandle>(null);
  const [token, setToken] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [waitedOut, setWaitedOut] = useState(false);
  // Once a token has ever arrived the widget demonstrably works, so the 10 s
  // fallback no longer applies: after a reset or an expiry we wait for the NEXT
  // token rather than send '' and earn a spurious "content blocker" message
  // (/qa Phase 5 L1).
  const [hadToken, setHadToken] = useState(false);
  const [resetCount, setResetCount] = useState(0);
  const [silentAfterReset, setSilentAfterReset] = useState(false);
  // Bumped by restart(): a form in a DIALOG that opens many times on one page needs
  // a fresh gate per opening (a spent token from the last one must never be resent).
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!siteKey) return undefined;
    setWaitedOut(false);
    const timer = setTimeout(() => setWaitedOut(true), TURNSTILE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [siteKey, generation]);

  // The safety valve after a reset (/qa Phase 6 L2): re-armed by every reset,
  // cancelled by the next token.
  useEffect(() => {
    if (!siteKey || resetCount === 0) return undefined;
    setSilentAfterReset(false);
    const timer = setTimeout(() => setSilentAfterReset(true), TURNSTILE_RESET_WAIT_MS);
    return () => clearTimeout(timer);
  }, [siteKey, resetCount]);

  const onToken = useCallback((next: string | null) => {
    setToken(next);
    if (next) {
      setHadToken(true);
      // A token proves the widget works NOW: an earlier error no longer opens the
      // gate, or a later reset would send '' (/qa Phase 6 L1).
      setUnavailable(false);
      setSilentAfterReset(false);
    }
  }, []);
  const onUnavailable = useCallback(() => setUnavailable(true), []);

  /** Call after every FAILED submit attempt: a token is single use, even when the request was refused. */
  const reset = useCallback(() => {
    widgetRef.current?.reset();
    setResetCount((n) => n + 1);
  }, []);

  /**
   * Forget everything and start over, as on a fresh page: call when a DIALOG that
   * holds the widget opens again (its widget is a new one; the old token is spent).
   */
  const restart = useCallback(() => {
    setToken(null);
    setHadToken(false);
    setUnavailable(false);
    setSilentAfterReset(false);
    setResetCount(0);
    setGeneration((g) => g + 1);
  }, []);

  const ready =
    !siteKey || token !== null || unavailable || (waitedOut && !hadToken) || silentAfterReset;

  return {
    widgetRef,
    onToken,
    onUnavailable,
    reset,
    restart,
    ready,
    /** The widget is still working on its check: the page says so beside a disabled button. */
    waiting: Boolean(siteKey) && !ready,
    /** What the form sends: the token, or '' (the server asks Cloudflare either way). */
    tokenForSubmit: token ?? '',
  };
}
